import { useMemo, useRef } from 'react';
import { Button } from '@carbon/react';
import classNames from 'classnames';
import { clamp } from 'lodash-es';
import { BoundInput } from '../bound-input';
import { useCategoryFields, useModalFacets } from '../template-hooks';
import {
  formatRangeValue,
  orderedBounds,
  parseRangeBound,
  readValueHistogram,
  roundRangeOutward,
  valueRangeLabel,
  type ValueHistogramFacet,
} from '../value-histogram';
import type {
  FilterTemplateProps,
  ValueHistogramFilterField,
} from '../../types';
import style from '../filter-modal.module.scss';

interface ValueHistogramRowProps {
  field: ValueHistogramFilterField;
  histogram: ValueHistogramFacet;
  formValues: Record<string, unknown>;
  onFormChange: (key: string, value: unknown) => void;
}

/**
 * The histogram, its axis ends, Min/Max and Full range for one field.
 * @param {ValueHistogramRowProps} props The field, its facet and the form.
 * @return {JSX.Element} The row.
 */
const ValueHistogramRow = ({
  field,
  histogram,
  formValues,
  onFormChange,
}: ValueHistogramRowProps) => {
  const [fullMin, fullMax] = roundRangeOutward([histogram.min, histogram.max]);
  const staged: [number | null, number | null] = [
    parseRangeBound(formValues[field.minParam]),
    parseRangeBound(formValues[field.maxParam]),
  ];
  // Flagged while back to front; applied as the span between the two either way.
  const invalid =
    staged[0] !== null && staged[1] !== null && staged[0] > staged[1];
  const [low, high] = orderedBounds(...staged);
  const units = histogram.units ? ` (${histogram.units})` : '';
  const unitSuffix = histogram.units ? ` ${histogram.units}` : '';
  const peak = useMemo(
    () => Math.max(1, ...histogram.bins.map((bin) => bin.count)),
    [histogram.bins]
  );

  // An end typed back to the histogram's own end is no filter at all, so it
  // unstages rather than leaving a no-op chip.
  const commit = (param: string, value: number | null, fullEnd: number) =>
    onFormChange(
      param,
      value === null || value === fullEnd ? undefined : String(value)
    );

  const fullRangeLabel = `Full range (${valueRangeLabel(
    histogram.min,
    histogram.max,
    histogram.units
  )})`;

  // Dragging across the distribution sets both bounds (PRD): the brushed band
  // shows as the active bars and the Min/Max inputs stay in sync.
  const dragStart = useRef<number | null>(null);
  // The value under the pointer. The two ends are exactly the full range (so a
  // full-width drag dims nothing); in between it snaps to a step of 0.1% of the
  // span, fine enough that a narrow range keeps its resolution.
  const valueAt = (clientX: number, target: HTMLElement): number => {
    const rect = target.getBoundingClientRect();
    const fraction = clamp(
      rect.width > 0 ? (clientX - rect.left) / rect.width : 0,
      0,
      1
    );
    if (fraction === 0) return fullMin;
    if (fraction === 1) return fullMax;
    const span = fullMax - fullMin;
    const raw = fullMin + fraction * span;
    if (span <= 0) return raw;
    const step = 10 ** (Math.floor(Math.log10(span)) - 3);
    const snapped = Number((Math.round(raw / step) * step).toPrecision(12));
    return clamp(snapped, fullMin, fullMax);
  };
  const cancelBrush = () => {
    dragStart.current = null;
  };
  const stageBrush = (from: number, to: number) => {
    const [brushLow, brushHigh] = orderedBounds(from, to);
    onFormChange(field.minParam, String(brushLow));
    onFormChange(field.maxParam, String(brushHigh));
  };
  const endBrush = (from: number, to: number) => {
    const [brushLow, brushHigh] = orderedBounds(from, to);
    // A dragged end at the histogram's own end is no filter on that side.
    commit(field.minParam, brushLow, fullMin);
    commit(field.maxParam, brushHigh, fullMax);
  };

  return (
    <div className={style.valueHistogram}>
      <span className={style.valueHistogramLabel}>
        {`${field.label}${units}`}
      </span>
      <div
        className={style.valueHistogramBars}
        onPointerDown={(event) => {
          // Only the primary button brushes; a right-click opens no drag.
          if (event.button !== 0) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          dragStart.current = valueAt(event.clientX, event.currentTarget);
          stageBrush(dragStart.current, dragStart.current);
        }}
        onPointerMove={(event) => {
          if (dragStart.current === null) return;
          stageBrush(
            dragStart.current,
            valueAt(event.clientX, event.currentTarget)
          );
        }}
        onPointerUp={(event) => {
          if (dragStart.current === null) return;
          endBrush(
            dragStart.current,
            valueAt(event.clientX, event.currentTarget)
          );
          dragStart.current = null;
        }}
        // A gesture the browser cancels (or capture lost mid-drag) ends the drag
        // where it is, so later hovering doesn't keep restaging the range.
        onPointerCancel={cancelBrush}
        onLostPointerCapture={cancelBrush}
        role="img"
        aria-label={`${field.label} histogram, ${formatRangeValue(
          histogram.min
        )} to ${formatRangeValue(histogram.max)}${unitSuffix}`}
      >
        {histogram.bins.map((bin, index) => {
          const inRange =
            (low === null || bin.x1 >= low) &&
            (high === null || bin.x0 <= high);
          return (
            <span
              // eslint-disable-next-line @eslint-react/no-array-index-key -- index, not x0: a flat histogram gives every bin the same bounds.
              key={index}
              className={classNames(style.valueHistogramBar, {
                [style.valueHistogramBarActive]: inRange,
              })}
              style={{ height: `${(bin.count / peak) * 100}%` }}
              title={`${valueRangeLabel(
                bin.x0,
                bin.x1,
                histogram.units
              )}: ${formatRangeValue(bin.count)} cells`}
            />
          );
        })}
      </div>
      <div className={style.valueHistogramAxis}>
        <span>{formatRangeValue(histogram.min)}</span>
        <span>{formatRangeValue(histogram.max)}</span>
      </div>
      <div className={style.valueHistogramBounds}>
        <BoundInput
          id={`filter-${field.minParam}`}
          label={`Min${units}`}
          staged={formValues[field.minParam]}
          fullValue={fullMin}
          invalid={invalid}
          onCommit={(value) => commit(field.minParam, value, fullMin)}
        />
        <BoundInput
          id={`filter-${field.maxParam}`}
          label={`Max${units}`}
          staged={formValues[field.maxParam]}
          fullValue={fullMax}
          invalid={invalid}
          onCommit={(value) => commit(field.maxParam, value, fullMax)}
        />
      </div>
      <div>
        <Button
          kind="ghost"
          size="sm"
          onClick={() => {
            onFormChange(field.minParam, undefined);
            onFormChange(field.maxParam, undefined);
          }}
        >
          {fullRangeLabel}
        </Button>
      </div>
    </div>
  );
};

/**
 * Value-histogram filters for a category: the matching maps' pooled histogram
 * with Min/Max and Full range. Staged like any filter but ignored by the backend;
 * the page applies it (Subsurface Maps dims cells outside it).
 * @param {FilterTemplateProps} props The category and form.
 * @return {JSX.Element} The template.
 */
export const ValueHistogramTemplate = ({
  category,
  formValues,
  onFormChange,
}: FilterTemplateProps) => {
  const fields = useCategoryFields(category, 'value-histogram');
  const facets = useModalFacets();

  return (
    <div>
      {fields.map((field) => {
        // Facets failing to load is not the same as no map having values.
        if (!facets) {
          return (
            <p key={field.id} className={style.valueHistogramEmpty}>
              Couldn’t load the value range. Close and reopen the filters to try
              again.
            </p>
          );
        }
        const histogram = readValueHistogram(facets, field.facetKey);
        if (!histogram) {
          return (
            <p key={field.id} className={style.valueHistogramEmpty}>
              No values to filter: none of the matching maps have values yet.
            </p>
          );
        }
        return (
          <ValueHistogramRow
            key={field.id}
            field={field}
            histogram={histogram}
            formValues={formValues}
            onFormChange={onFormChange}
          />
        );
      })}
    </div>
  );
};
